'use client';

import {
  OfferCardProps,
  OfferProvider,
  OfferGridSkeleton,
} from '@wwe-portal/ui/features/offer';
import { SubscriptionTier } from '@wwe-portal/entities/subscription.type';
import { ExclusiveItemsSectionRevamp as ExclusiveItemsSectionUI } from '@wwe-portal/widget/club-store';

import {
  isBlockOffer,
  subCategoryTypeToTier,
} from '@wwe-portal/web/utils/club-store/mapper';
import { ExclusiveItemResponse } from '@wwe-portal/entities/club-store.type';
import { useCallback, useMemo } from 'react';
import { useExclusiveDialogRevamp } from '@wwe-portal/web/hooks/club-store/use-exclusive-dialog';
import { useExclusiveStoreData } from '@wwe-portal/web/hooks/club-store/useExclusiveStoreData';
import {
  useGetCurrentUserId,
  useIsAuthenticated,
} from '@wwe-portal/web/providers/stores/auth-store';
import usePurchaseByClubCoins from '../../hooks/club-store/usePurchaseByClubCoins';
import { useEffectOnceWhen } from '@wwe-portal/web/hooks/common/useEffectOnceWhen';
import { sendExclusiveItemsRevampLoadedEventSysUI } from '@wwe-portal/web/libs/analytics/club-store';
import { OfferData } from '@wwe-portal/entities/offer.type';
import {
  useSegmentedOfferFilter,
  useGetUserStocks,
} from '@wwe-portal/web/providers/queries/offer';
import {
  getStockQuantity,
  offerToProps,
} from '@wwe-portal/web/utils/offer/mapper';

const selectOfferData = ({ offerData }: ExclusiveItemResponse) => offerData;

export const ExclusiveItemsSection = () => {
  const isAuthenticated = useIsAuthenticated();
  const { filterSegmentedOffers } = useSegmentedOfferFilter();
  const { stockMap } = useGetUserStocks();
  const { purchaseItemsByClubCoins, isPending: isLoading } =
    usePurchaseByClubCoins({
      onError: () => {
        closeExclusiveDialog();
      },
      onPaymentSuccess: () => {
        closeExclusiveDialog();
      },
    });

  const handleOfferBuy = (data: {
    tier: SubscriptionTier | undefined;
    offerData: OfferData | OfferCardProps;
  }) => {
    getValidationPurchase({
      tier: data.tier,
      offerData: data.offerData as OfferData,
      onFailedCallback: () => {
        closeExclusiveDialog();
      },
    });
  };

  const {
    exclusiveDialog,
    setExclusiveDialogData,
    closeDialog: closeExclusiveDialog,
  } = useExclusiveDialogRevamp({
    isLoading,
    handleBuy: handleOfferBuy,
  });

  const {
    titles,
    descriptions,
    enabled,
    getValidationPurchase,
    currentPlan,
    exclusiveItemResponse,
    isLoading: isLoadingExclusiveItems,
  } = useExclusiveStoreData({
    closeExclusiveDialog,
    purchaseItemsByClubCoins,
  });

  const findOffer = ({ OffersID }: { OffersID: string }) =>
    exclusiveItemResponse?.find((item) => item.offerData.OffersID === OffersID);
  const userId = useGetCurrentUserId();

  const handlePurchaseClick = ({ OffersID }: { OffersID: string }, e: any) => {
    const offer = findOffer({ OffersID });
    if (!offer) return;
    const quantity = getStockQuantity(offer.offerData, {
      stockMap,
      isAuthenticated,
    });
    if (quantity) {
      const offerData = offer.offerData;
      const tier = subCategoryTypeToTier(offer.subCategoryType);
      const isBlocked = isBlockOffer(offer.subCategoryType, currentPlan);
      setExclusiveDialogData({
        offerData,
        tier,
        isBlocked,
      });
      if (!isAuthenticated) {
        e['isCancelledClickOffer'] = true;
      }
      getValidationPurchase({
        tier,
        offerData,
        onFailedCallback: () => {
          closeExclusiveDialog();
        },
      });
    }
  };

  const handleOfferClick = ({ OffersID }: { OffersID: string }, e: any) => {
    if (e['isCancelledClickOffer']) return;
    const offer = findOffer({ OffersID });
    if (!offer) return;
    const offerData = offer.offerData;
    const tier = subCategoryTypeToTier(offer.subCategoryType);
    const isBlocked = isBlockOffer(offer.subCategoryType, currentPlan);
    setExclusiveDialogData({
      offerData,
      tier,
      isBlocked,
    });
  };

  const originOffers = useMemo(() => {
    const exclusiveOffers = exclusiveItemResponse?.map((offer) => ({
      ...offer,
      OffersID: offer.offerId,
    }));

    return filterSegmentedOffers(exclusiveOffers) ?? [];
  }, [exclusiveItemResponse, filterSegmentedOffers]);

  const exclusiveItemData = useMemo(() => {
    if (!originOffers?.length) return {};
    const itemsByTier = {
      [SubscriptionTier.GOLD]: originOffers
        .filter((offer) => offer.subCategoryType === 3)
        .map(selectOfferData),
      [SubscriptionTier.SILVER]: originOffers
        .filter((offer) => offer.subCategoryType === 2)
        .map(selectOfferData),
      [SubscriptionTier.BRONZE]: originOffers
        .filter((offer) => offer.subCategoryType === 1)
        .map(selectOfferData),
    };
    const offerIdsByTier = new Map<string, SubscriptionTier>();
    Object.values(SubscriptionTier).forEach((tier) => {
      itemsByTier[tier].forEach((offerData) => {
        offerIdsByTier.set(offerData.OffersID, tier);
      });
    });
    return {
      itemsByTier,
      offerIdsByTier,
    };
  }, [originOffers]);

  const parseExclusiveItemToRevampProps = useCallback(
    (offerData: OfferData) => {
      const tier = exclusiveItemData?.offerIdsByTier?.get(offerData.OffersID);

      const parsedOffer = offerToProps(offerData, {
        stockMap,
        isAuthenticated,
      });

      const props: OfferCardProps = {
        ...parsedOffer,
        soldOut: (parsedOffer.quantity ?? 0) < 1,
        isBlocked: isBlockOffer(tier, currentPlan),
      };
      return props;
    },
    [currentPlan, exclusiveItemData, stockMap, isAuthenticated]
  );

  useEffectOnceWhen(() => {
    if (exclusiveItemData?.itemsByTier && userId) {
      sendExclusiveItemsRevampLoadedEventSysUI(
        exclusiveItemData.itemsByTier,
        userId
      );
    }
  }, !!exclusiveItemData?.itemsByTier && !!userId);

  if (isLoadingExclusiveItems) {
    return <OfferGridSkeleton total={12} />;
  }

  return (
    <OfferProvider
      offerToProps={parseExclusiveItemToRevampProps}
      onBannerClick={function (): void {
        throw new Error('Function not implemented.');
      }}
      onBannerMoreClick={function (): void {
        throw new Error('Function not implemented.');
      }}
      onOfferClick={handleOfferClick}
      onOfferPriceClick={handlePurchaseClick}
    >
      <ExclusiveItemsSectionUI
        exclusiveItems={exclusiveItemData.itemsByTier}
        titles={titles}
        enabled={enabled}
        descriptions={descriptions}
      />
      {exclusiveDialog}
    </OfferProvider>
  );
};
